import type { ReactElement, ReactNode } from 'react';
import { ColumnTypeSchema, formatCell, type ColumnType, type DatasetColumn } from '@tallyframe/core';
import { Checkbox } from '../ui/Checkbox';
import { InputField } from '../ui/Input';
import { SelectField } from '../ui/Select';

const COLUMN_TYPES: ColumnType[] = [...ColumnTypeSchema.options];

/** Plain-language names for the column types (spec §9.3): no raw type keys
 *  on screen. */
export const TYPE_LABEL: Record<DatasetColumn['type'], string> = {
  number: 'Number',
  integer: 'Whole number',
  date: 'Date',
  string: 'Text',
  boolean: 'Yes/No'
};

const TYPE_OPTIONS = COLUMN_TYPES.map((type) => ({ key: type, label: TYPE_LABEL[type] }));

const MUTED_TEXT = 'm-0 p-0 font-ui text-sm text-muted';
const SECTION_HEADING = 'm-0 p-0 font-ui text-md font-semibold text-ink';
const GRID = 'm-0 p-0 border-collapse font-ui text-sm text-ink';
const GRID_HEAD = 'm-0 px-2 py-1 border-0 border-b border-solid border-line text-left font-semibold text-muted';
const GRID_CELL = 'm-0 px-2 py-1 border-0 border-b border-solid border-line text-left align-middle';

export interface ColumnStepProps {
  columns: DatasetColumn[];
  sampleRows: Array<Record<string, unknown>>;
  title: string;
  grain: string;
  onTitle(v: string): void;
  onGrain(v: string): void;
  onColumn(index: number, patch: Partial<DatasetColumn>): void;
  disabled?: boolean;
  /** Wording under the Columns heading; defaults to a source-neutral one. */
  columnsHint?: string;
  /** Muted line between the Sample rows heading and the table. */
  sampleCaption?: ReactNode;
}

/**
 * Column confirmation: report title, grain, the column review table and a
 * sample of the rows. It owns no file or source, so the CSV and Sheet flows
 * share it.
 */
export function ColumnStep(props: ColumnStepProps): ReactElement {
  const { columns, sampleRows } = props;
  return (
    <>
      <div className="m-0 p-0 flex flex-wrap items-start gap-4">
        <InputField
          className="grow"
          label="Report title"
          value={props.title}
          maxLength={200}
          onChange={props.onTitle}
          isDisabled={props.disabled}
        />
        <InputField
          className="grow"
          label="What does exactly one row represent?"
          value={props.grain}
          maxLength={500}
          onChange={props.onGrain}
          isDisabled={props.disabled}
        />
      </div>

      <div className="m-0 p-0 flex flex-col gap-2">
        <h3 className={SECTION_HEADING}>Columns</h3>
        <p className={MUTED_TEXT}>{props.columnsHint ?? 'Inferred from the source. Correct anything that is wrong.'}</p>
        <div className="m-0 p-0 overflow-x-auto">
          <table className={GRID} data-testid="csv-column-review">
            <thead>
              <tr>
                <th scope="col" className={GRID_HEAD}>
                  Name
                </th>
                <th scope="col" className={GRID_HEAD}>
                  Type
                </th>
                <th scope="col" className={GRID_HEAD}>
                  Nullable
                </th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column, index) => (
                <tr key={column.id}>
                  <td className={GRID_CELL}>
                    <InputField
                      label={`Name for column ${column.id}`}
                      labelHidden
                      value={column.label}
                      maxLength={200}
                      onChange={(label) => props.onColumn(index, { label })}
                      isDisabled={props.disabled}
                    />
                  </td>
                  <td className={GRID_CELL}>
                    <SelectField
                      label={`Type for column ${column.id}`}
                      options={TYPE_OPTIONS}
                      value={column.type}
                      onChange={(type) => props.onColumn(index, { type: type as ColumnType })}
                    />
                  </td>
                  <td className={GRID_CELL}>
                    <Checkbox
                      isSelected={column.nullable}
                      isDisabled={props.disabled}
                      onChange={(nullable) => props.onColumn(index, { nullable })}
                    >
                      may be empty
                    </Checkbox>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="m-0 p-0 flex flex-col gap-2">
        <h3 className={SECTION_HEADING}>Sample rows</h3>
        {props.sampleCaption === undefined ? null : <p className={MUTED_TEXT}>{props.sampleCaption}</p>}
        <div className="m-0 p-0 overflow-x-auto">
          <table className={GRID}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.id} scope="col" className={GRID_HEAD}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sampleRows.map((row, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td key={column.id} className={GRID_CELL}>
                      {formatCell(row[column.id] ?? null, undefined)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
